import { useState, useEffect, useMemo } from 'react'
import { useShoppingCart } from '../context/ShoppingCart'
import { getStoredDistributionCenter } from '../utils/store'
import { slugify } from '../utils/format'
import { getProductsBySkus, FormattedProduct } from '../services/Algolia'

/**
 * Hook to validate if cart has products with "personalize_me" tag
 * @returns object with validation state and products data
 */
export const useCartPersonalizeMeValidation = () => {
  const { state } = useShoppingCart()
  const { byHash } = state
  const [algoliaProducts, setAlgoliaProducts] = useState<FormattedProduct[]>([])

  const cartSkus = useMemo(() => Object.keys(byHash), [byHash])
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const fetchProductsWithTags = async () => {
      if (cartSkus.length === 0) {
        setAlgoliaProducts([])
        setIsInitialized(true)
        return
      }

      const distributionCenter = getStoredDistributionCenter()
      if (!distributionCenter) {
        setIsInitialized(true)
        return
      }

      try {
        const slugLocation = slugify(distributionCenter.commerceLayer.stockLocation.name)

        const products = await getProductsBySkus({
          slugLocation,
          skus: cartSkus,
        })

        setAlgoliaProducts(products)
      } catch (error) {
        setAlgoliaProducts([])
      } finally {
        setIsInitialized(true)
      }
    }

    fetchProductsWithTags()
  }, [cartSkus])

  const values = useMemo(() => {
    const hasPersonalizeMeItems = algoliaProducts.some((product) => product && product.tags === 'personalize_me')
    const totalSkipHours = hasPersonalizeMeItems ? 72 : 0

    return {
      hasPersonalizeMeItems,
      totalSkipHours,
      algoliaProducts,
      isInitialized,
      cartItems: byHash,
    }
  }, [algoliaProducts, isInitialized, byHash])

  return values
}
